import { useState, useEffect, useRef, useMemo, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Dialog, Transition } from '@headlessui/react';
import {
  MagnifyingGlassIcon,
  ChartBarIcon,
  ChartPieIcon,
  DocumentTextIcon,
  BellIcon,
  ServerIcon,
  CursorArrowRaysIcon,
  BugAntIcon,
  UsersIcon,
  UserGroupIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  GlobeAltIcon,
  CloudIcon,
  BeakerIcon,
  RocketLaunchIcon,
  CpuChipIcon,
  XMarkIcon,
  CommandLineIcon,
  FireIcon,
  MapIcon,
  BoltIcon,
  ExclamationTriangleIcon,
  SignalIcon,
  ClockIcon,
  ShieldCheckIcon,
  HomeIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  Cog6ToothIcon,
  FunnelIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';

type Category = 'overview' | 'mobile' | 'web' | 'infra' | 'intelligence' | 'account' | 'admin';

interface QuickAction {
  id: string;
  title: string;
  url: string;
  icon: any;
  category: Category;
  keywords: string[];
}

// Normaliza para busca: remove acentos + minúsculas (ex.: "Métricas" -> "metricas")
const norm = (s: string) =>
  (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

const CATEGORY_STYLES: Record<Category, { chip: string; tile: string }> = {
  overview: { chip: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300', tile: 'from-indigo-500 to-indigo-600' },
  mobile: { chip: 'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300', tile: 'from-pink-500 to-rose-600' },
  web: { chip: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300', tile: 'from-sky-500 to-blue-600' },
  infra: { chip: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', tile: 'from-emerald-500 to-teal-600' },
  intelligence: { chip: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300', tile: 'from-violet-500 to-purple-600' },
  account: { chip: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', tile: 'from-amber-500 to-orange-600' },
  admin: { chip: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', tile: 'from-red-500 to-rose-600' },
};

export default function GlobalSearch() {
  const { t } = useTranslation(['common', 'settings']);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const getCategoryLabel = (category: Category) => t(`search.categories.${category}`, category);

  // Índice de busca — espelha o sidebar (plataforma: Mobile / Web / Infra ...)
  const quickActions: QuickAction[] = useMemo(() => [
    // Visão geral
    { id: 'dashboard', title: t('nav.dashboard', 'Dashboard'), url: '/dashboard', icon: HomeIcon, category: 'overview', keywords: ['dashboard', 'inicio', 'home', 'visao geral', 'resumo', 'overview'] },

    // Mobile
    { id: 'analytics-mobile', title: t('nav.analyticsMobile', 'Analytics Mobile'), url: '/analytics/mobile/journeys', icon: DevicePhoneMobileIcon, category: 'mobile', keywords: ['mobile', 'app', 'aplicativo', 'celular', 'android', 'ios', 'react native', 'rum mobile', 'analytics mobile', 'analitico mobile'] },
    { id: 'mobile-journeys', title: t('nav.mobileJourneys', 'Jornadas Mobile'), url: '/analytics/mobile/journeys', icon: MapIcon, category: 'mobile', keywords: ['mobile', 'app', 'jornadas mobile', 'journeys', 'fluxo', 'telas', 'app'] },
    { id: 'mobile-setup', title: t('nav.mobileSetup', 'Configuração SDK Mobile'), url: '/analytics/mobile/setup', icon: RocketLaunchIcon, category: 'mobile', keywords: ['mobile', 'app', 'sdk mobile', 'setup mobile', 'instalar', 'configurar', 'integracao', 'react native', 'expo', 'android', 'ios'] },

    // Web / Site
    { id: 'analytics-web', title: t('nav.analyticsWeb', 'Analytics Web'), url: '/analytics/overview', icon: ComputerDesktopIcon, category: 'web', keywords: ['web', 'site', 'produto', 'analytics', 'analitico', 'product analytics'] },
    { id: 'analytics-overview', title: t('nav.overview', 'Visão Geral'), url: '/analytics/overview', icon: ChartPieIcon, category: 'web', keywords: ['overview', 'visao geral', 'resumo', 'analytics'] },
    { id: 'rum', title: 'Real User Monitoring (RUM)', url: '/analytics/rum', icon: CursorArrowRaysIcon, category: 'web', keywords: ['rum', 'real user monitoring', 'sessoes', 'sessions', 'frontend', 'browser', 'experiencia', 'web vitals'] },
    { id: 'events', title: t('nav.analyticsEvents', 'Eventos'), url: '/analytics/events', icon: BoltIcon, category: 'web', keywords: ['events', 'eventos', 'tracking', 'rastreamento'] },
    { id: 'funnels', title: t('nav.analyticsFunnels', 'Funis'), url: '/analytics/funnels', icon: FunnelIcon, category: 'web', keywords: ['funnels', 'funis', 'conversao', 'conversion', 'etapas'] },
    { id: 'retention', title: t('nav.analyticsRetention', 'Retenção'), url: '/analytics/retention', icon: UserGroupIcon, category: 'web', keywords: ['retention', 'retencao', 'coortes', 'cohorts', 'churn'] },
    { id: 'geography', title: t('nav.analyticsGeography', 'Geografia'), url: '/analytics/geography', icon: GlobeAltIcon, category: 'web', keywords: ['geography', 'geografia', 'paises', 'mapa', 'regioes', 'localizacao'] },
    { id: 'devices', title: t('nav.analyticsDevices', 'Dispositivos'), url: '/analytics/devices', icon: CpuChipIcon, category: 'web', keywords: ['devices', 'dispositivos', 'navegadores', 'browsers', 'sistema'] },
    { id: 'pages', title: t('nav.analyticsPages', 'Páginas'), url: '/analytics/pages', icon: DocumentTextIcon, category: 'web', keywords: ['pages', 'paginas', 'urls'] },
    { id: 'traffic', title: t('nav.analyticsTraffic', 'Fontes de Tráfego'), url: '/analytics/traffic', icon: ArrowTrendingUpIcon, category: 'web', keywords: ['traffic', 'trafego', 'fontes', 'referrer', 'utm', 'campanhas'] },
    { id: 'web-journeys', title: t('nav.analyticsJourneys', 'Jornada Web'), url: '/analytics/journeys', icon: MapIcon, category: 'web', keywords: ['journeys', 'jornada web', 'fluxo', 'path', 'caminho'] },
    { id: 'sdk-setup', title: t('nav.sdkSetup', 'Configuração do SDK'), url: '/analytics/sdk-setup', icon: RocketLaunchIcon, category: 'web', keywords: ['sdk', 'setup', 'instalacao', 'snippet', 'web sdk', 'integracao', 'configurar'] },
    { id: 'cloud-scan', title: t('nav.cloudScan', 'Cloud Scanner'), url: '/cloud-scan', icon: CloudIcon, category: 'web', keywords: ['cloud', 'scan', 'scanner', 'aws', 'azure', 'gcp', 'seguranca cloud'] },
    { id: 'security', title: t('nav.security', 'Scanner de Segurança'), url: '/security', icon: ShieldCheckIcon, category: 'web', keywords: ['security', 'seguranca', 'scanner', 'pentest', 'owasp', 'xss', 'sqli', 'vulnerabilidade'] },
    { id: 'seo', title: t('nav.seoScanner', 'SEO Scanner'), url: '/seo-scanner', icon: MagnifyingGlassIcon, category: 'web', keywords: ['seo', 'scanner', 'meta tags', 'busca', 'google'] },

    // Servidores & Infra
    { id: 'apm', title: t('nav.apm', 'APM'), url: '/apm', icon: ChartBarIcon, category: 'infra', keywords: ['apm', 'performance', 'backend', 'traces', 'monitoramento'] },
    { id: 'service-map', title: t('nav.serviceMap', 'Mapa de Serviços'), url: '/apm/service-map', icon: MapIcon, category: 'infra', keywords: ['service map', 'mapa de servicos', 'dependencias', 'microservices', 'topologia'] },
    { id: 'traces', title: t('nav.traces', 'Traces'), url: '/apm/traces', icon: SignalIcon, category: 'infra', keywords: ['traces', 'tracing', 'spans', 'distributed tracing'] },
    { id: 'transactions', title: t('nav.transactions', 'Transações'), url: '/apm/transactions', icon: ClockIcon, category: 'infra', keywords: ['transactions', 'transacoes', 'endpoints', 'operacoes'] },
    { id: 'apm-errors', title: t('nav.errors', 'Erros'), url: '/apm/errors', icon: BugAntIcon, category: 'infra', keywords: ['errors', 'erros', 'exceptions', 'crashes', 'bugs', 'falhas'] },
    { id: 'database', title: t('nav.database', 'Banco de Dados'), url: '/apm/database', icon: ServerIcon, category: 'infra', keywords: ['database', 'banco de dados', 'sql', 'queries', 'consultas'] },
    { id: 'logs', title: t('nav.logs', 'Logs'), url: '/logs', icon: DocumentTextIcon, category: 'infra', keywords: ['logs', 'logging', 'mensagens'] },
    { id: 'infrastructure', title: t('nav.infrastructure', 'Infraestrutura'), url: '/infrastructure/hosts', icon: ServerIcon, category: 'infra', keywords: ['infrastructure', 'infraestrutura', 'hosts', 'servidores', 'servers', 'maquinas'] },
    { id: 'containers', title: t('nav.containers', 'Containers'), url: '/infrastructure/containers', icon: CpuChipIcon, category: 'infra', keywords: ['containers', 'docker', 'conteineres'] },
    { id: 'kubernetes', title: t('nav.kubernetes', 'Kubernetes'), url: '/infrastructure/kubernetes', icon: CloudIcon, category: 'infra', keywords: ['kubernetes', 'k8s', 'pods', 'clusters', 'nodes'] },
    { id: 'firewall-logs', title: t('nav.logCollection', 'Firewall Logs'), url: '/infrastructure/log-collection', icon: ShieldCheckIcon, category: 'infra', keywords: ['firewall', 'logs', 'cisco', 'fortigate', 'coleta de logs'] },
    { id: 'metrics', title: t('nav.metrics', 'Métricas'), url: '/metrics', icon: ChartPieIcon, category: 'infra', keywords: ['metrics', 'metricas', 'graficos', 'timeseries'] },
    { id: 'agents', title: t('nav.agents', 'Agentes'), url: '/agents', icon: CpuChipIcon, category: 'infra', keywords: ['agents', 'agentes', 'host agent', 'coletor'] },
    { id: 'status-pages', title: t('nav.statusPages', 'Páginas de Status'), url: '/status-pages', icon: GlobeAltIcon, category: 'infra', keywords: ['status', 'status page', 'pagina de status', 'publico', 'incidentes'] },
    { id: 'uptime', title: t('nav.uptime', 'Monitor de Uptime'), url: '/uptime', icon: SignalIcon, category: 'infra', keywords: ['uptime', 'monitor', 'disponibilidade', 'ping', 'health check'] },

    // Inteligência
    { id: 'alerts', title: t('nav.alerts', 'Alertas'), url: '/alerts', icon: BellIcon, category: 'intelligence', keywords: ['alerts', 'alertas', 'notificacoes', 'avisos'] },
    { id: 'ml-insights', title: t('nav.insights', 'Insights'), url: '/ml/insights', icon: BeakerIcon, category: 'intelligence', keywords: ['insights', 'ia', 'ai', 'ml', 'machine learning', 'inteligencia'] },
    { id: 'anomalies', title: t('nav.anomalies', 'Anomalias'), url: '/ml/anomalies', icon: ExclamationTriangleIcon, category: 'intelligence', keywords: ['anomalies', 'anomalias', 'outliers', 'deteccao', 'ia'] },
    { id: 'forecasts', title: t('nav.forecasts', 'Previsões'), url: '/ml/forecasts', icon: ArrowTrendingUpIcon, category: 'intelligence', keywords: ['forecasts', 'previsoes', 'predicoes', 'tendencias', 'ia'] },

    // Conta
    { id: 'applications', title: t('settings:menu.applications', 'Aplicações'), url: '/settings/applications', icon: RocketLaunchIcon, category: 'account', keywords: ['aplicacoes', 'aplicacao', 'applications', 'apps', 'projetos', 'app', 'criar aplicacao', 'sdk', 'api key'] },
    { id: 'team', title: t('settings:menu.team', 'Equipe'), url: '/settings/users', icon: UsersIcon, category: 'account', keywords: ['equipe', 'usuarios', 'team', 'membros', 'users', 'pessoas', 'permissoes'] },
    { id: 'api-keys', title: t('settings:menu.apiKeys', 'Chaves de API'), url: '/settings/api-keys', icon: CommandLineIcon, category: 'account', keywords: ['api', 'keys', 'chaves', 'tokens', 'credenciais', 'api key'] },
    { id: 'billing', title: t('settings:menu.billing', 'Faturamento'), url: '/settings/billing', icon: CreditCardIcon, category: 'account', keywords: ['faturamento', 'billing', 'plano', 'assinatura', 'pagamento', 'cobranca', 'fatura'] },
    { id: 'affiliate', title: t('nav.affiliate', 'Afiliados'), url: '/affiliate/dashboard', icon: UserGroupIcon, category: 'account', keywords: ['affiliate', 'afiliados', 'earn', 'comissoes', 'parceiros', 'ganhar'] },
    { id: 'settings', title: t('nav.settings', 'Configurações'), url: '/settings', icon: Cog6ToothIcon, category: 'account', keywords: ['settings', 'configuracoes', 'preferencias', 'ajustes', 'conta', 'perfil'] },

    // Admin
    { id: 'admin-plans', title: t('nav.adminPlans', 'Gerenciar Planos'), url: '/admin/plans', icon: CreditCardIcon, category: 'admin', keywords: ['plans', 'planos', 'precos', 'admin'] },
    { id: 'admin-tenants', title: t('nav.adminCustomers', 'Clientes'), url: '/admin/tenants', icon: GlobeAltIcon, category: 'admin', keywords: ['tenants', 'clientes', 'organizacoes', 'admin'] },
    { id: 'admin-users', title: t('nav.adminUsers', 'Usuários'), url: '/admin/users', icon: UsersIcon, category: 'admin', keywords: ['users', 'usuarios', 'admin', 'gerenciar usuarios'] },
    { id: 'admin-subscriptions', title: t('nav.adminSubscriptions', 'Assinaturas'), url: '/admin/subscriptions', icon: CreditCardIcon, category: 'admin', keywords: ['subscriptions', 'assinaturas', 'contratos', 'admin'] },
    { id: 'admin-invoices', title: t('nav.adminInvoices', 'Faturas'), url: '/admin/invoices', icon: CurrencyDollarIcon, category: 'admin', keywords: ['invoices', 'faturas', 'notas fiscais', 'admin'] },
  ], [t]);

  // Busca ranqueada, sem acento e por tokens (todos os termos precisam casar)
  const filteredActions = useMemo(() => {
    const q = norm(searchTerm);
    if (q.length < 1) return quickActions;
    const tokens = q.split(/\s+/).filter(Boolean);

    const scored = quickActions
      .map((action) => {
        const title = norm(action.title);
        const kws = action.keywords.map(norm);
        const cat = norm(getCategoryLabel(action.category));
        const hay = `${title} ${kws.join(' ')} ${cat}`;
        if (!tokens.every((tk) => hay.includes(tk))) return { action, score: 0 };

        let score = 0;
        if (title === q) score += 1000;
        else if (title.startsWith(q)) score += 300;
        else if (title.includes(q)) score += 150;
        for (const tk of tokens) {
          if (title.startsWith(tk)) score += 40;
          else if (title.includes(tk)) score += 20;
          if (kws.some((k) => k === tk)) score += 30;
          else if (kws.some((k) => k.startsWith(tk))) score += 12;
          else if (kws.some((k) => k.includes(tk))) score += 6;
        }
        score -= title.length * 0.1; // desempate: prefere títulos mais curtos
        return { action, score };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score);

    return scored.map((x) => x.action);
  }, [searchTerm, quickActions]);

  const popularPages = useMemo(() => {
    const ids = ['dashboard', 'analytics-mobile', 'analytics-web', 'apm', 'logs', 'alerts'];
    return ids.map((id) => quickActions.find((a) => a.id === id)).filter(Boolean) as QuickAction[];
  }, [quickActions]);

  const suggestions = useMemo(() => {
    const ids = ['analytics-mobile', 'mobile-setup', 'rum', 'apm'];
    return ids.map((id) => quickActions.find((a) => a.id === id)).filter(Boolean) as QuickAction[];
  }, [quickActions]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (selectedIndex > filteredActions.length - 1) setSelectedIndex(0);
  }, [filteredActions.length, selectedIndex]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, filteredActions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && filteredActions[selectedIndex]) {
      e.preventDefault();
      handleActionClick(filteredActions[selectedIndex]);
    }
  };

  const handleActionClick = (action: QuickAction) => {
    navigate(action.url);
    setIsOpen(false);
    setSearchTerm('');
    setSelectedIndex(0);
  };

  const showingResults = searchTerm.trim().length > 0;

  return (
    <>
      {/* Search Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="flex w-full max-w-md items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-500 shadow-sm transition-all hover:border-indigo-400 hover:shadow-md dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:border-indigo-500"
      >
        <MagnifyingGlassIcon className="h-5 w-5 flex-shrink-0" />
        <span className="flex-1 text-left text-xs truncate">{t('search.placeholder')}</span>
        <kbd className="hidden items-center gap-0.5 rounded border border-gray-300 bg-gray-50 px-1.5 py-0.5 text-xs font-medium text-gray-600 sm:inline-flex dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400">
          <CommandLineIcon className="h-3 w-3" />
          <span>K</span>
        </kbd>
      </button>

      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
            leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-md" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-start justify-center p-4 pt-[12vh]">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200" enterFrom="opacity-0 scale-95 translate-y-2" enterTo="opacity-100 scale-100 translate-y-0"
                leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl border border-gray-200/60 bg-white/95 shadow-2xl ring-1 ring-black/5 backdrop-blur-xl transition-all dark:border-gray-700/60 dark:bg-gray-900/95">
                  {/* Search Input */}
                  <div className="flex items-center gap-3 border-b border-gray-200 px-4 py-3.5 dark:border-gray-800">
                    <MagnifyingGlassIcon className="h-5 w-5 flex-shrink-0 text-indigo-500" />
                    <input
                      ref={inputRef}
                      type="text"
                      value={searchTerm}
                      onChange={(e) => { setSearchTerm(e.target.value); setSelectedIndex(0); }}
                      onKeyDown={handleKeyDown}
                      placeholder={t('search.placeholderModal')}
                      className="flex-1 border-0 bg-transparent text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0 dark:text-white dark:placeholder-gray-500"
                      autoFocus
                    />
                    {searchTerm && (
                      <span className="hidden flex-shrink-0 text-xs text-gray-400 sm:inline dark:text-gray-500">
                        {t('search.resultsCount', { count: filteredActions.length })}
                      </span>
                    )}
                    <button
                      onClick={() => setIsOpen(false)}
                      className="flex-shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="max-h-[58vh] overflow-y-auto p-2">
                    {!showingResults ? (
                      /* Estado inicial: páginas populares + dicas */
                      <div className="p-2">
                        <div className="mb-2 flex items-center gap-2 px-2">
                          <FireIcon className="h-4 w-4 text-orange-500" />
                          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                            {t('search.popularPages')}
                          </h3>
                        </div>
                        <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                          {popularPages.map((action) => {
                            const Icon = action.icon;
                            const st = CATEGORY_STYLES[action.category];
                            return (
                              <button
                                key={action.id}
                                onClick={() => handleActionClick(action)}
                                className="group flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-2.5 text-left transition-all hover:border-indigo-300 hover:shadow-sm dark:border-gray-800 dark:bg-gray-800/40 dark:hover:border-indigo-700"
                              >
                                <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${st.tile} text-white shadow-sm`}>
                                  <Icon className="h-5 w-5" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="truncate text-sm font-medium text-gray-900 dark:text-white">{action.title}</p>
                                  <p className="truncate text-xs text-gray-400 dark:text-gray-500">{getCategoryLabel(action.category)}</p>
                                </div>
                              </button>
                            );
                          })}
                        </div>

                        <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-500 dark:bg-gray-800/50 dark:text-gray-400">
                          <span className="inline-flex items-center gap-1.5"><kbd className="rounded border border-gray-300 bg-white px-1.5 py-0.5 font-mono dark:border-gray-600 dark:bg-gray-700">↑↓</kbd>{t('search.tips.navigate')}</span>
                          <span className="inline-flex items-center gap-1.5"><kbd className="rounded border border-gray-300 bg-white px-1.5 py-0.5 font-mono dark:border-gray-600 dark:bg-gray-700">↵</kbd>{t('search.tips.select')}</span>
                          <span className="inline-flex items-center gap-1.5"><kbd className="rounded border border-gray-300 bg-white px-1.5 py-0.5 font-mono dark:border-gray-600 dark:bg-gray-700">Esc</kbd>{t('search.tips.close')}</span>
                        </div>
                      </div>
                    ) : filteredActions.length > 0 ? (
                      /* Resultados ranqueados (lista única) */
                      <div className="space-y-0.5">
                        {filteredActions.map((action, idx) => {
                          const isSelected = idx === selectedIndex;
                          const Icon = action.icon;
                          const st = CATEGORY_STYLES[action.category];
                          return (
                            <button
                              key={action.id}
                              onClick={() => handleActionClick(action)}
                              onMouseEnter={() => setSelectedIndex(idx)}
                              className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
                                isSelected ? 'bg-indigo-50 dark:bg-indigo-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-800/60'
                              }`}
                            >
                              <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${
                                isSelected ? `bg-gradient-to-br ${st.tile} text-white shadow-md` : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                              }`}>
                                <Icon className="h-5 w-5" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium text-gray-900 dark:text-white">{action.title}</p>
                                <p className="truncate font-mono text-[11px] text-gray-400 dark:text-gray-500">{action.url}</p>
                              </div>
                              <span className={`hidden flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide sm:inline-block ${st.chip}`}>
                                {getCategoryLabel(action.category)}
                              </span>
                              {isSelected && (
                                <kbd className="hidden flex-shrink-0 rounded border border-indigo-300 bg-indigo-100 px-1.5 py-0.5 text-xs font-medium text-indigo-700 sm:block dark:border-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">↵</kbd>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      /* Sem resultados: mensagem + sugestões (nunca fica num beco) */
                      <div className="px-4 py-10 text-center">
                        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 dark:bg-gray-800">
                          <MagnifyingGlassIcon className="h-7 w-7 text-gray-400" />
                        </div>
                        <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-white">{t('search.noResults.title')}</h3>
                        <p className="mx-auto mt-1.5 max-w-md text-sm text-gray-500 dark:text-gray-400">
                          {t('search.noResults.description', { query: searchTerm })}
                        </p>
                        <div className="mt-5">
                          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">{t('search.suggestions', 'Sugestões')}</p>
                          <div className="flex flex-wrap justify-center gap-2">
                            {suggestions.map((action) => {
                              const Icon = action.icon;
                              const st = CATEGORY_STYLES[action.category];
                              return (
                                <button
                                  key={action.id}
                                  onClick={() => handleActionClick(action)}
                                  className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 transition-all hover:border-indigo-300 hover:shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-indigo-600"
                                >
                                  <span className={`flex h-5 w-5 items-center justify-center rounded bg-gradient-to-br ${st.tile} text-white`}>
                                    <Icon className="h-3.5 w-3.5" />
                                  </span>
                                  {action.title}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
